import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { Countdown } from './Countdown';
import { ShieldCheck, Check, Flame, Star, Sparkles } from 'lucide-react';
type OfferId = 'basic' | 'upsell';
interface Offer {
  id: OfferId;
  name: string;
  shortDesc: string;
  image: string;
  oldPrice: string;
  price: string;
  includes: string[];
  redirectUrl: string;
  badge?: string;
}
const offers: Offer[] = [{
  id: 'basic',
  name: 'KIT Psicólogo Deportivo',
  shortDesc: 'Sistema para estructurar sesiones de rendimiento mental.',
  image: 'https://cdn.magicpatterns.com/uploads/2fusPuyD529iw7oHCdFpMC/Diseno_sin_titulo_(24).png',
  oldPrice: '$49.990',
  price: '$19.990',
  includes: ['Método Mente y Rendimiento™', 'Recursos de Intervención Deportiva', 'Herramientas Profesionales Aplicables', 'BONUS: Comunicación con el Cuerpo Técnico', 'BONUS: Protocolo de Primera Sesión'],
  redirectUrl: 'https://checkout.sectordigital.online/cart/44063671025707:1'
}, {
  id: 'upsell',
  name: 'KIT + Protocolo Frente a la Derrota',
  shortDesc: 'Kit completo + herramientas para post-derrota y resiliencia.',
  image: 'https://cdn.magicpatterns.com/uploads/3JwEFSsww8NceA1D6Evxz6/Diseno_sin_titulo_(25).png',
  oldPrice: '$69.990',
  price: '$29.990',
  includes: ['Todo lo del KIT Psicólogo Deportivo', 'Protocolo Frente a la Derrota', 'Estrategias de Reencuadre Cognitivo', 'Gestión Emocional & Foco', 'Planificación Post-Derrota & Aprendizaje'],
  redirectUrl: 'https://checkout.sectordigital.online/cart/44075518525483:1',
  badge: 'MÁS ELEGIDA'
}];
export function PurchaseSection() {
  const [selected, setSelected] = useState<OfferId>('upsell');
  const selectedOffer = offers.find(o => o.id === selected)!;
  return <section className="bg-slate-950 min-h-[100svh] flex items-center py-4 md:py-8 relative overflow-hidden" data-id="element-137">
      <div className="absolute inset-0 opacity-20 pointer-events-none" data-id="element-138">
        <div className="absolute top-1/2 left-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-600 blur-[120px]" data-id="element-139"></div>
      </div>

      <div className="container relative mx-auto px-3 sm:px-6 lg:px-8 w-full" data-id="element-140">
        <div className="mx-auto max-w-5xl" data-id="element-141">
          <div id="comprar" className="bg-white rounded-2xl md:rounded-[2rem] p-3.5 md:p-6 shadow-2xl scroll-mt-2 border border-slate-100" data-id="element-142">
            {/* Countdown */}
            <div className="mb-3 md:mb-3" data-id="element-143">
              <Countdown data-id="element-144" />
            </div>

            {/* Value strip */}
            <div className="mb-3 md:mb-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60 px-3 py-2 md:px-4 md:py-2.5 flex items-center gap-2" data-id="element-145">
              <div className="flex h-6 w-6 md:h-8 md:w-8 shrink-0 items-center justify-center rounded-full bg-amber-400 text-amber-900" data-id="element-146">
                <Sparkles className="h-3 w-3 md:h-4 md:w-4" strokeWidth={2.5} data-id="element-147" />
              </div>
              <p className="text-[10.5px] md:text-sm text-slate-700 leading-tight font-medium" data-id="element-148">
                <span className="font-bold text-slate-900" data-id="element-149">
                  Sumá el Protocolo Frente a la Derrota
                </span>{' '}
                y trabajá el peor momento del deportista.
              </p>
            </div>

            {/* Main content: image + offers */}
            <div className="grid gap-3 md:gap-5 md:grid-cols-[1fr_1.2fr] items-stretch" data-id="element-150">
              {/* Product Image (dynamic) */}
              <div className="relative flex items-center justify-center bg-slate-50 rounded-xl md:rounded-2xl p-3 md:p-6 min-h-[110px] md:min-h-[320px] border border-slate-100" data-id="element-151">
                <AnimatePresence mode="wait" data-id="element-152">
                  <motion.img key={selectedOffer.id} src={selectedOffer.image} alt={selectedOffer.name} initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.95
                }} transition={{
                  duration: 0.2
                }} className="max-h-[105px] md:max-h-[300px] w-auto object-contain drop-shadow-xl" data-id="element-153" />
                </AnimatePresence>
              </div>

              {/* Right column: offers + CTA */}
              <div className="flex flex-col gap-2.5 md:gap-3 justify-between" data-id="element-154">
                {/* Offer Selection */}
                <div className="grid gap-2 md:gap-2.5" data-id="element-155">
                  {offers.map(offer => {
                  const isSelected = selected === offer.id;
                  return <button key={offer.id} type="button" onClick={() => setSelected(offer.id)} className={`relative text-left rounded-xl transition-all duration-200 border-2 ${isSelected ? 'border-amber-400 bg-amber-50/40 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'} p-3 md:p-3.5`} data-id="element-156">
                        {offer.badge && <div className="absolute -top-2 right-2 flex items-center gap-1 rounded-full bg-amber-400 px-1.5 py-0.5 text-[9px] md:text-xs font-black text-slate-900 shadow-sm whitespace-nowrap" data-id="element-157">
                            <Flame className="h-2.5 w-2.5" data-id="element-158" />
                            {offer.badge}
                          </div>}

                        <div className="flex items-center gap-2" data-id="element-159">
                          <div className={`flex h-5 w-5 md:h-6 md:w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${isSelected ? 'border-amber-400 bg-amber-400' : 'border-slate-300 bg-white'}`} data-id="element-160">
                            {isSelected && <Check className="h-3 w-3 md:h-3.5 md:w-3.5 text-white" strokeWidth={3} data-id="element-161" />}
                          </div>

                          <div className="flex-1 min-w-0" data-id="element-162">
                            <h3 className="font-bold text-slate-900 text-[13px] md:text-sm leading-tight" data-id="element-163">
                              {offer.name}
                            </h3>
                            <p className="mt-0.5 text-[10px] leading-tight text-slate-500 md:text-xs" data-id="element-164">
                              {offer.shortDesc}
                            </p>
                          </div>

                          <div className="text-right shrink-0" data-id="element-165">
                            <div className="text-[9px] md:text-xs text-slate-400 line-through font-bold leading-none" data-id="element-166">
                              {offer.oldPrice}
                            </div>
                            <div className="text-[16px] md:text-lg font-black text-slate-900 font-display leading-tight" data-id="element-167">
                              {offer.price}
                            </div>
                          </div>
                        </div>
                      </button>;
                })}
                </div>

                {/* Selected offer includes */}
                <AnimatePresence mode="wait" data-id="element-168">
                  <motion.div key={selectedOffer.id} initial={{
                  opacity: 0,
                  y: 4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -4
                }} transition={{
                  duration: 0.18
                }} className="rounded-lg bg-slate-50 px-2.5 py-2.5 md:px-3.5 md:py-3 border border-slate-100" data-id="element-169">
                    <p className="mb-1.5 text-[10px] md:text-xs font-bold uppercase tracking-wide text-slate-700" data-id="element-170">
                      Incluye:
                    </p>
                    <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2" data-id="element-171">
                      {selectedOffer.includes.map(item => {
                      const isHighlighted = item.includes('BONUS') || item.includes('Protocolo Frente');
                      return <li key={item} className="flex items-start gap-1.5 text-[10px] md:text-xs leading-tight text-slate-600" data-id="element-172">
                            <Check className={`mt-px h-3 w-3 shrink-0 ${isHighlighted ? 'text-amber-500' : 'text-emerald-600'}`} strokeWidth={3} data-id="element-173" />
                            <span className={isHighlighted ? 'font-semibold text-amber-700' : 'font-medium'} data-id="element-174">
                              {item}
                            </span>
                          </li>;
                    })}
                    </ul>
                  </motion.div>
                </AnimatePresence>

                {/* CTA block */}
                <div className="rounded-xl bg-slate-950 p-3.5 md:p-4 text-center mt-auto" data-id="element-175">
                  <div className="flex items-center justify-center gap-1.5 mb-2 md:mb-2" data-id="element-176">
                    <span className="text-slate-400 font-bold text-sm md:text-sm line-through decoration-red-500/60 decoration-2" data-id="element-177">
                      {selectedOffer.oldPrice}
                    </span>
                    <span className="text-2xl md:text-3xl font-black text-white font-display leading-none" data-id="element-178">
                      {selectedOffer.price}
                    </span>
                    <span className="text-[11px] md:text-sm text-slate-400 font-bold" data-id="element-179">
                      ARS
                    </span>
                  </div>

                  <Button redirectUrl={selectedOffer.redirectUrl} className="w-full text-sm md:text-base py-3 md:py-3.5 mb-2 md:mb-2" data-id="element-180">
                    SÍ, QUIERO EL KIT AHORA
                  </Button>

                  <div className="flex flex-wrap items-center justify-center gap-x-2 md:gap-x-4 gap-y-0.5 text-[9px] md:text-xs font-medium text-slate-400" data-id="element-181">
                    <div className="flex items-center gap-0.5" data-id="element-182">
                      <ShieldCheck className="h-2.5 w-2.5 text-emerald-400" data-id="element-183" />{' '}
                      Pago único
                    </div>
                    <div className="flex items-center gap-0.5" data-id="element-184">
                      <Check className="h-2.5 w-2.5 text-emerald-400" data-id="element-185" /> Acceso
                      inmediato
                    </div>
                    <div className="flex items-center gap-0.5" data-id="element-186">
                      <Star className="h-2.5 w-2.5 text-amber-400 fill-amber-400" data-id="element-187" />{' '}
                      +500 prof.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}